import React, { useMemo } from 'react';
import AttendanceFlame from 'app/javascript/components/shared/AttendanceFlame';
import { useCheckInHeatmap } from 'app/javascript/queries/useProfiles';
import {
  getHeatmapBucketKey,
  getHeatmapThresholds,
  HEATMAP_BUCKET_STYLES,
} from 'app/javascript/services/heatmap';
import {
  formatIsoDate,
  formatWeekRange,
  MONTH_LABELS_PT_SHORT,
  parseIsoDate,
} from 'app/javascript/utils/dates';
import { Popover, PopoverContent, PopoverTrigger } from 'app/javascript/components/ui/popover';

interface CheckInHeatmapProps {
  slug: string;
  companySlug: string;
  startDate?: string; // YYYY-MM-DD inclusive
  endDate?: string; // YYYY-MM-DD inclusive (defaults to today)
}

interface WeekData {
  weekStart: string; // Monday of the week (for stable React keys)
  weekEnd: string; // Sunday of the week
  coveredFrom: string; // first day of the week actually counted (== weekStart unless clipped)
  coveredTo: string; // last day of the week actually counted (== weekEnd unless clipped)
  count: number;
  monthLabel: string | null;
  yearLabel: string | null;
}

const DEFAULT_NUM_WEEKS = 10;
const MAX_WEEKS = 156; // ~3 years cap

function getMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

interface BuildOptions {
  startDate?: string;
  endDate?: string;
}

interface ResolvedRange {
  // Counting bounds — only these days count toward week totals.
  countFrom: Date;
  countTo: Date;
  // Display bounds — weeks rendered are firstMonday..lastMonday (inclusive).
  firstMonday: Date;
  lastMonday: Date;
}

// Resolves the heatmap's range in two modes:
// - Open-ended (no startDate/endDate): default look-back; counts whole Mon→Sun weeks.
// - Clipped (caller passed dates): narrows to first/last actual check-in inside the
//   requested window; counts only days within that clipped span.
function resolveRange(checkIns: Record<string, number>, opts: BuildOptions): ResolvedRange | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const requestedEnd = opts.endDate ? parseIsoDate(opts.endDate) : today;
  if (!requestedEnd) return null;
  if (requestedEnd > today) requestedEnd.setTime(today.getTime());

  let requestedStart: Date | null;
  if (opts.startDate) {
    requestedStart = parseIsoDate(opts.startDate);
  } else {
    requestedStart = new Date(requestedEnd);
    requestedStart.setDate(requestedStart.getDate() - (DEFAULT_NUM_WEEKS - 1) * 7);
  }
  if (!requestedStart || requestedStart > requestedEnd) return null;

  const clipToRange = !!(opts.startDate || opts.endDate);
  let countFrom = requestedStart;
  let countTo = requestedEnd;

  if (clipToRange) {
    // Narrow to actual check-in dates inside the requested window.
    const inRange = Object.keys(checkIns).filter((dateKey) => {
      if (!checkIns[dateKey]) return false;
      const d = parseIsoDate(dateKey);
      return d !== null && d >= requestedStart! && d <= requestedEnd;
    });
    if (inRange.length === 0) return null;
    inRange.sort();
    const first = parseIsoDate(inRange[0]);
    const last = parseIsoDate(inRange[inRange.length - 1]);
    if (!first || !last) return null;
    countFrom = first;
    countTo = last;
  } else {
    // Open-ended: count whole Mon→Sun weeks.
    countFrom = getMonday(requestedStart);
    countTo = new Date(getMonday(requestedEnd));
    countTo.setDate(countTo.getDate() + 6);
  }

  return {
    countFrom,
    countTo,
    firstMonday: getMonday(countFrom),
    lastMonday: getMonday(countTo),
  };
}

function buildWeeklyData(checkIns: Record<string, number>, opts: BuildOptions): WeekData[] {
  const resolved = resolveRange(checkIns, opts);
  if (!resolved) return [];
  const { countFrom, countTo, firstMonday, lastMonday } = resolved;

  const totalMs = lastMonday.getTime() - firstMonday.getTime();
  const totalWeeks = Math.min(MAX_WEEKS, Math.floor(totalMs / (7 * 24 * 60 * 60 * 1000)) + 1);

  const weeks: WeekData[] = [];
  let lastMonth = -1;
  let lastYear = -1;

  for (let i = 0; i < totalWeeks; i++) {
    const weekStart = new Date(firstMonday);
    weekStart.setDate(weekStart.getDate() + i * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const coveredFromDate = weekStart < countFrom ? countFrom : weekStart;
    const coveredToDate = weekEnd < countTo ? weekEnd : countTo;

    let count = 0;
    const cursor = new Date(coveredFromDate);
    while (cursor <= coveredToDate) {
      const key = formatIsoDate(cursor);
      if (checkIns[key] && checkIns[key] > 0) count++;
      cursor.setDate(cursor.getDate() + 1);
    }

    const month = weekStart.getMonth();
    const year = weekStart.getFullYear();
    const monthLabel = month !== lastMonth ? MONTH_LABELS_PT_SHORT[month] : null;
    const yearLabel = year !== lastYear ? String(year) : null;
    lastMonth = month;
    lastYear = year;

    weeks.push({
      weekStart: formatIsoDate(weekStart),
      weekEnd: formatIsoDate(weekEnd),
      coveredFrom: formatIsoDate(coveredFromDate),
      coveredTo: formatIsoDate(coveredToDate),
      count,
      monthLabel,
      yearLabel,
    });
  }

  return weeks;
}

const CheckInHeatmap: React.FC<CheckInHeatmapProps> = ({ slug, companySlug, startDate, endDate }) => {
  const { data, isPending, isError } = useCheckInHeatmap(slug, companySlug);
  // NOTE: buildWeeklyData calls new Date() internally for the default endDate.
  // A page kept open across midnight won't recompute the bucket boundaries
  // until React Query refetches; accepted today since refetches are common
  // and the visual drift is one cell.
  const weeks = useMemo(() => {
    if (!data) return null;
    return buildWeeklyData(data.check_ins, { startDate, endDate });
  }, [data, startDate, endDate]);

  if (isPending) {
    return (
      <div className="mt-3">
        <div className="h-[52px] animate-pulse rounded-lg bg-slate-800/20" />
      </div>
    );
  }

  if (isError || !data || !weeks || weeks.length === 0) {
    return null;
  }

  const thresholds = getHeatmapThresholds(data.weekly_class_days);

  return (
    <div className="mt-3">
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Month + year labels row (year joins the first month of each year) */}
          <div className="mb-1 flex items-end gap-1.5">
            {weeks.map((week, i) => (
              <div key={`m-${i}`} className="min-w-0 flex-1">
                {week.monthLabel && (
                  <span className="text-sm text-slate-400">
                    {week.monthLabel}
                    {week.yearLabel && (
                      <span className="font-semibold text-slate-200">/{week.yearLabel}</span>
                    )}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Weekly strip */}
          <div className="flex gap-1.5">
            {weeks.map((week) => {
              const bucketKey = getHeatmapBucketKey(week.count, thresholds);
              const style = HEATMAP_BUCKET_STYLES[bucketKey];
              const isFull = bucketKey === 'full' && week.count > 0;
              // For boundary weeks (first/last when clipped) coveredFrom/coveredTo
              // are the actually-counted span; popover stays consistent with the count.
              const range = formatWeekRange(week.coveredFrom, week.coveredTo);
              const dayWord = week.count === 1 ? 'dia' : 'dias';
              const ariaLabel =
                week.count === 0
                  ? `${range}: sem treinos`
                  : `${range}: ${week.count} ${dayWord} de treino`;

              return (
                <Popover key={`w-${week.weekStart}`}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={ariaLabel}
                      className={`flex h-7 min-w-[18px] flex-1 items-center justify-center rounded transition-all duration-150 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60 ${style.bg}`}
                    >
                      {week.count > 0 &&
                        (isFull ? (
                          <span className={`flex items-center gap-1 text-xs font-bold ${style.text}`}>
                            {week.count}
                            <AttendanceFlame className={style.text} />
                          </span>
                        ) : (
                          <span className={`text-xs font-bold ${style.text}`}>{week.count}</span>
                        ))}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="w-72">
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wide text-slate-400">Semana</p>
                      <p className="text-sm font-semibold text-white">{range}</p>
                      <p className="text-sm text-white/85">
                        {week.count === 0
                          ? 'Nenhum treino nesta semana.'
                          : `Você treinou em ${week.count} ${dayWord} nesta semana.`}
                      </p>
                      <p className="text-xs leading-relaxed text-slate-400">
                        Contamos apenas dias distintos de treino — múltiplos check-ins no mesmo dia contam como 1.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInHeatmap;
